import React, { useState } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import useReportFormat from "../useReportFormat";

const TYPE_COLORS = {
  APT:        { bg: "#dce8ff", color: "#002045", dot: "#3b82f6" },
  OVER_PARK:  { bg: "#e8f5e9", color: "#1b5e20", dot: "#22c55e" },
  UNDER_PARK: { bg: "#f3e8ff", color: "#5b21b6", dot: "#a855f7" },
  STO:        { bg: "#fff3e0", color: "#7c2d12", dot: "#f97316" },
  LAND:       { bg: "#fce4ec", color: "#880e4f", dot: "#ec4899" },
  SAD:        { bg: "#e8eaf6", color: "#1a237e", dot: "#6366f1" },
};

const DONUT_COLORS = ["#3b82f6", "#22c55e", "#a855f7", "#f97316", "#ec4899", "#6366f1"];

const SoldBar = ({ sold, total, color }) => {
  const pct = total > 0 ? Math.round((sold / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
      <div style={{ flex: 1, height: 5, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width 0.4s ease" }} />
      </div>
      <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#64748b", minWidth: 32, textAlign: "right" }}>{pct}%</span>
    </div>
  );
};

const MiniDonut = ({ segments }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const series = [{ data: segments.map((seg, i) => ({ id: i, value: seg.value, color: seg.color })), innerRadius: 26, outerRadius: 40, paddingAngle: 2, cornerRadius: 2 }];

  return (
    <div style={{ position: "relative", width: 90, height: 90, flexShrink: 0 }}>
      <PieChart
        series={series}
        width={90}
        height={90}
        slotProps={{ legend: { hidden: true } }}
        tooltip={{ trigger: "none" }}
        margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
      />
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#002045", lineHeight: 1 }}>{total}</span>
        <span style={{ fontSize: 8, color: "#94a3b8", lineHeight: 1.4 }}>units</span>
      </div>
    </div>
  );
};

const UnitTypeBreakdown = ({ data = [] }) => {
  const [expanded, setExpanded] = useState({});
  const formatCurrency = useReportFormat();

  if (!data.length) return null;

  const donutSegments = data.map((d, i) => ({ value: d.total, color: DONUT_COLORS[i % DONUT_COLORS.length] }));

  return (
    <div style={{ background: "white", borderRadius: 16, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <p style={{ fontWeight: 700, fontSize: "0.95rem", color: "#002045", margin: 0 }}>Unit Breakdown</p>
          <p style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 500, margin: "2px 0 0" }}>
            Sales by type
          </p>
        </div>
        <MiniDonut segments={donutSegments} />
      </div>

      {/* Rows */}
      <div>
        {data.map((row, i) => {
          const cfg   = TYPE_COLORS[row.unit_type] || { bg: "#f8fafc", color: "#374151", dot: "#94a3b8" };
          const isExp = expanded[row.unit_type];
          const hasRooms = row.rooms?.length > 0;

          return (
            <div key={row.unit_type}>
              <div
                style={{ padding: "14px 24px", borderBottom: "1px solid #f8fafc", display: "flex", alignItems: "center", gap: 12, cursor: hasRooms ? "pointer" : "default" }}
                onClick={() => hasRooms && setExpanded((p) => ({ ...p, [row.unit_type]: !p[row.unit_type] }))}
              >
                {/* Dot */}
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: DONUT_COLORS[i % DONUT_COLORS.length], flexShrink: 0 }} />

                {/* Label */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#002045" }}>{row.label}</span>
                      <span style={{ background: cfg.bg, color: cfg.color, fontSize: "0.62rem", fontWeight: 700, borderRadius: 99, padding: "1px 7px" }}>
                        {row.sold}/{row.total} sold
                      </span>
                      {hasRooms && <i className={`ri-arrow-${isExp ? "up" : "down"}-s-line`} style={{ color: "#94a3b8", fontSize: "0.75rem" }} />}
                    </div>
                    <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#002045" }}>{formatCurrency(row.listing_value)}</span>
                  </div>
                  <SoldBar sold={row.sold} total={row.total} color={DONUT_COLORS[i % DONUT_COLORS.length]} />
                </div>
              </div>

              {/* Rooms expansion */}
              {isExp && row.rooms?.map((rm) => (
                <div key={rm.num_rooms} style={{ padding: "10px 24px 10px 44px", borderBottom: "1px solid #f8fafc", display: "flex", alignItems: "center", gap: 12, background: "#fafbff" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#93c5fd", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155" }}>{rm.label}</span>
                        <span style={{ background: "#dce8ff", color: "#002045", fontSize: "0.6rem", fontWeight: 700, borderRadius: 99, padding: "1px 6px" }}>
                          {rm.sold}/{rm.total}
                        </span>
                      </div>
                      <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}>{formatCurrency(rm.listing_value)}</span>
                    </div>
                    <SoldBar sold={rm.sold} total={rm.total} color="#93c5fd" />
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UnitTypeBreakdown;
